import { getFontOptionSync } from 'api/google-fonts';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useVATReturnFrequency from 'hooks/shared/useVATReturnFrequency';
import useFeatures from 'hooks/useFeatures';
import { pick } from 'lodash';
import { useTranslation } from 'react-i18next';
import { RevenueClient } from 'types/clients.types';
import { CurrenciesEnum } from 'types/global.types';
import {
  GeneratedInvoiceFormValues,
  Invoice,
  InvoiceFileTypeEnum,
  InvoiceItemsCalculations,
  InvoiceTemplatesEnum,
  InvoiceTypeEnum,
} from 'types/invoice.types';
import { RevenueStatus } from 'types/revenues.types';
import { AvailableLanguages, IUser, VATType } from 'types/users.types';
import { invoiceDefaultFont } from 'utils/constants';
import { itemFieldsNames } from '../SharedControls/ItemsForm/helpers';

const defaultInvoiceDate = dayjs().format('YYYY-MM-DD');

export function useRevenueDefaultValuesFn() {
  const customerLanguage = useTranslation().i18n.language as AvailableLanguages;
  const customer = useGetCurrentCustomerData().data as IUser;

  const isUsingBankReference = !!useFeatures('revenue.use_bank_reference');
  const dueDate = useFeatures('revenue.defaultDueDate');
  const hasDeliveryDate = !!useFeatures('revenue.has_delivery_date');
  const hasPaymentDate = !!useFeatures('revenue.has_payment_date');
  const hasSteuernummer = !!useFeatures('revenue.has_streuernummer');
  const VATReturnFrequency = useVATReturnFrequency();

  return (
    defaults?: Partial<Invoice<RevenueClient>> & {
      itemsCalculations?: InvoiceItemsCalculations;
    },
  ): GeneratedInvoiceFormValues => {
    const {
      VATNumber,
      address,
      companyName,
      phoneNumber,
      email,
      firstName,
      lastName,
      settings,
      IBAN,
      SWIFT,
      VATType,
      steuernummer,
    } = defaults?.userSnapshot || customer || {};

    const { city, zip, street, state } = address || {};

    const logo = { filePath: settings?.invoices.logo?.filePath };

    const termsAndConditions = (() => {
      const value = settings?.invoices.termsAndConditions;
      if (!value) return '';
      if (typeof value === 'string') return value;
      return value.default || '';
    })();

    const templateType =
      settings?.invoices?.templateType || InvoiceTemplatesEnum.rounded;

    const fontFamily = settings?.invoices?.fontFamily || invoiceDefaultFont;
    const items = defaults?.items || [];

    const withDeliveryPeriod = !!defaults?.deliveryPeriod;
    const withDeliveryDate = !!defaults?.deliveryDate;

    const showDeliveryPeriod =
      hasDeliveryDate &&
      (withDeliveryPeriod ||
        (!withDeliveryDate && !!settings?.invoices?.showDeliveryPeriod));

    const type = defaults?.type || InvoiceTypeEnum.INVOICE;

    const hasCopyRightCategory = items.some(
      (item) => item.categoryId === 'be.revenue.copyright',
    );

    const currency = defaults?.currency || CurrenciesEnum.EUR;

    return {
      _id: defaults?._id,
      isFake: !!defaults?.isFake,
      type,
      fileType: InvoiceFileTypeEnum.generated,
      currency,
      items: items.map((item, i) => ({
        ...pick(item, Object.values(itemFieldsNames)),
        _localId: 'default_' + i,
      })),
      itemsCalculations: defaults?.itemsCalculations,
      paymentAmount:
        defaults?.paymentAmount != null
          ? defaults.paymentAmount
          : currency !== CurrenciesEnum.EUR
          ? defaults?.baseCurrencyTotalAmountInclVAT // in case we create an invoice from a quote
          : undefined,
      client: defaults?.client,
      clientId: defaults?.client?._id,
      comments: defaults?.comments || undefined,
      din5008: defaults?.din5008,
      _saveSettings: true,
      settings: {
        doesUnitPriceIncludeVAT: !!settings?.invoices?.doesUnitPriceIncludeVAT,
        usePaymentQrCode:
          settings?.invoices?.usePaymentQrCode ??
          (type === InvoiceTypeEnum.INVOICE && !!IBAN),
        useCommunication:
          settings?.invoices?.useCommunication ?? isUsingBankReference,
        useDownpayment: !!defaults?.downPayment,
        _defaultUseDownpayment: !!defaults?.downPayment,
        includeCopyrightReminder:
          defaults?.userSnapshot?.settings?.invoices
            ?.includeCopyrightReminder ??
          (hasCopyRightCategory ||
            (customer?.settings?.invoices?.includeCopyrightReminder ?? true)),
        termsAndConditions,
        logo,
        templateType,
        language: settings?.invoices?.language || customerLanguage,
        header: {
          color: settings?.invoices?.header?.color || {
            r: 216,
            g: 92,
            b: 83,
          },
        },
        fontFamily,
        showDeliveryPeriod,
        _fontFallback: invoiceDefaultFont,
        _fontOption: getFontOptionSync(fontFamily),
      },
      user: {
        companyName: companyName || undefined,
        address: {
          city: city || undefined,
          street: street || undefined,
          zip: zip || undefined,
          // the backend doesnt seem to save the state in the userSnapshot
          state: state || customer?.address.state || undefined,
        },
        VATNumber: VATNumber || undefined,
        phoneNumber,
        email,
        steuernummer: hasSteuernummer ? steuernummer || undefined : undefined,
        SWIFT,
        IBAN: IBAN?.replace(/ /g, ''),
        VATReturnFrequency,
        VATType: VATType as VATType,
        firstName,
        lastName,
      },
      quoteId: defaults?.quoteId,
      deliveryDate:
        hasDeliveryDate && showDeliveryPeriod
          ? undefined
          : defaults?.deliveryDate || defaultInvoiceDate,
      deliveryPeriod:
        hasDeliveryDate && showDeliveryPeriod
          ? defaults?.deliveryPeriod
          : undefined,
      paymentType: defaults?.paymentType,
      paymentDate: hasPaymentDate ? defaults?.paymentDate : undefined,

      period: defaults?.period,
      transactions: defaults?.transactions || [],
      _matchedTransactions: defaults?._matchedTransactions || [],
      status:
        defaults?.status === RevenueStatus.draft
          ? RevenueStatus.not_sent
          : defaults?.status || RevenueStatus.not_sent,
      revenueNumber: defaults?.revenueNumber,
      invoiceDate: defaults?.invoiceDate || defaultInvoiceDate,
      orderReference: defaults?.orderReference,
      defaultRevenueNumber: defaults?.revenueNumber,
      dueDate: defaults?.dueDate || dueDate,
      downPayment: defaults?.downPayment,
      isSuggestedPeriod: defaults?.isSuggestedPeriod,
    };
  };
}

function useRevenueDefaultValues(
  defaults?: Partial<Invoice<RevenueClient>> & {
    itemsCalculations?: InvoiceItemsCalculations;
  },
): GeneratedInvoiceFormValues {
  return useRevenueDefaultValuesFn()(defaults);
}
export default useRevenueDefaultValues;
